import { TLShapeId } from '@tldraw/tldraw'
import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import { FrameShape } from './Types'
import { getOrderedFrames, insertFrame } from './helpers'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { CopyRight2 } from 'blaze-ui/icons'
import { useConfirmModal } from 'hooks/useConfirmModal'
import { DuplicateConfirmModal } from './DuplicateConfirmModal'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

interface Props {
  id: TLShapeId
  supportsCaption?: boolean
}

export const Duplicate = ({ id, supportsCaption = false }: Props) => {
  const editor = useEditor()
  const shape = editor.getShape<FrameShape>(id)
  const duplicateConfirm = useConfirmModal(DuplicateConfirmModal)
  const isSystemRedesignEnabled = useIsSystemRedesign()

  if (!shape) return null

  const handlePress = () => {
    const orderedFrames = getOrderedFrames(editor)
    editor.batch(async () => {
      const confirm = await duplicateConfirm()

      if (!confirm) {
        return
      }

      editor.duplicateShapes([id])
      // TODO: check if there's a more reliable way to get the shape just created
      const newFrame = editor.getOnlySelectedShape()
      if (!newFrame) return
      const reorderedFrames = insertFrame(
        orderedFrames,
        newFrame as FrameShape,
        orderedFrames[orderedFrames.length - 1].props.position + 1,
        supportsCaption,
      )
      editor.updateShapes(reorderedFrames)

      const newFrameBounds = editor.getShapePageBounds(newFrame)

      if (!newFrameBounds) {
        return
      }

      editor.zoomToBounds(newFrameBounds, undefined, { duration: 500, easing: (t) => t * (2 - t) })
      showToastMessage('Page successfully copied')
    })
  }

  return (
    <Tooltip label="Duplicate" placement="left">
      <IconButton
        variant="ghost"
        size="sm"
        icon={() => <CopyRight2 size={isSystemRedesignEnabled ? 16 : 20} />}
        aria-label="Duplicate"
        onPress={handlePress}
      />
    </Tooltip>
  )
}
